import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { FormGroupContainer } from '@/components/FormContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import {
  areSelectOptionsEqual,
  type SelectOption,
} from '@/components/inputs/select-option'
import {
  type EditRefundDialogViewModel,
  REFUND_TAB_LABELS,
} from './EditRefundDialogViewModel'
import { type TaxRefundMethodEditorViewModel } from '@/features/jobs/info-gathering/tax-refund-method/TaxRefundMethodEditorViewModel'
import {
  type BankAccountEditorViewModel,
  type BankAccountTypeOption,
} from '@/features/jobs/info-gathering/tax-refund-method/BankAccountEditorViewModel'
import { type CheckDeliveryMethodEditorViewModel } from '@/features/jobs/info-gathering/tax-refund-method/CheckDeliveryMethodEditorViewModel'
import { PhysicalAddressEditorContainer } from '@/features/jobs/info-gathering/components/PhysicalAddressEditorContainer'
import { InfoGatheringDialogFieldGrid } from '@/features/jobs/info-gathering/components/InfoGatheringDialog'
import { type TaxRefundDeliveryMethodType } from '@/features/jobs/info-gathering/tax-refund-method/TaxRefundDeliveryMethod'
import {
  EditRefundDialog,
  EditRefundDirectDepositSection,
  type EditRefundMethodOption,
  EditRefundMethodPicker,
  EditRefundSameAddressCheckbox,
} from '@/features/jobs/info-gathering/tax-refund-method/components/EditRefundDialog'

const ACCOUNT_TYPE_OPTIONS: SelectOption[] = [
  { id: 'Checking', text: 'Checking' },
  { id: 'Savings', text: 'Savings' },
]

const METHOD_OPTIONS: Record<
  TaxRefundDeliveryMethodType,
  EditRefundMethodOption<TaxRefundDeliveryMethodType>
> = {
  DirectDeposit: {
    id: 'DirectDeposit',
    label: 'Direct deposit',
    testId: 'edit-refund-method-directDeposit',
  },
  Check: {
    id: 'Check',
    label: 'Paper check',
    testId: 'edit-refund-method-check',
  },
  FutureTaxCredit: {
    id: 'FutureTaxCredit',
    label: 'Future tax credit',
    testId: 'edit-refund-method-futureTaxCredit',
  },
}

const accountTypeItemToString = (item: SelectOption) => item.text

export interface EditRefundDialogContainerProps {
  vm: EditRefundDialogViewModel
}

export const EditRefundDialogContainer = observer(
  function EditRefundDialogContainer({ vm }: EditRefundDialogContainerProps) {
    return (
      <EditRefundDialog
        showing={vm.modal.showing}
        onRequestClose={vm.submit.pending ? undefined : vm.modal.close}
        hasMultipleTabs={vm.hasMultipleTabs}
        tabs={vm.availableTabs.map((tab) => ({
          id: tab,
          label: REFUND_TAB_LABELS[tab],
        }))}
        activeTab={vm.activeTab}
        activeTabLabel={REFUND_TAB_LABELS[vm.activeTab]}
        onTabChange={vm.setTab}
        personalEditor={
          <RefundEditorContainer vm={vm.personalRefundEditorVm} />
        }
        businessEditor={
          <RefundEditorContainer vm={vm.businessRefundEditorVm} />
        }
        showBack={vm.hasMultipleTabs && !vm.isFirstTab}
        onBack={vm.goToPreviousTab}
        showSkip={vm.hasMultipleTabs && !vm.isLastTab}
        onSkip={vm.skipCurrentTab}
        submitLabel={vm.isLastTab ? 'Save & Close' : 'Save & Continue'}
        onSubmit={vm.submit}
        isSubmitPending={vm.submit.pending}
      />
    )
  },
)

interface RefundEditorContainerProps {
  vm: TaxRefundMethodEditorViewModel
}

const RefundEditorContainer = observer(function RefundEditorContainer({
  vm,
}: RefundEditorContainerProps) {
  return (
    <EditRefundMethodPicker
      methods={vm.availableMethods.map((method) => METHOD_OPTIONS[method])}
      selectedMethod={vm.selectedMethod}
      onSelectMethod={vm.selectMethod}
      selectionError={vm.selectionError}
    >
      {vm.selectedMethod === 'DirectDeposit' && (
        <DirectDepositFieldsContainer vm={vm.directDepositVm.bankAccountVm} />
      )}
      {vm.selectedMethod === 'Check' && (
        <CheckFieldsContainer vm={vm.checkVm} />
      )}
    </EditRefundMethodPicker>
  )
})

interface DirectDepositFieldsContainerProps {
  vm: BankAccountEditorViewModel
}

const DirectDepositFieldsContainer = observer(
  function DirectDepositFieldsContainer({
    vm,
  }: DirectDepositFieldsContainerProps) {
    const accountTypeItemSource = useMemo(
      () =>
        ItemSource.static<SelectOption>(ACCOUNT_TYPE_OPTIONS, (item, text) =>
          item.text.toLowerCase().includes(text.toLowerCase()),
        ),
      [],
    )
    const accountTypeSelectProps = useCustomSelectStateFromItemSource({
      itemSource: accountTypeItemSource,
    })
    const selectedAccountType = ACCOUNT_TYPE_OPTIONS.find(
      (o) => o.id === vm.accountType,
    )
    return (
      <EditRefundDirectDepositSection>
        <FormGroupContainer
          label="Account type"
          htmlFor="accountType"
          error={vm.validation.getError('accountType')}
        >
          <CustomSelect<SelectOption, false>
            {...accountTypeSelectProps}
            multi={false}
            value={selectedAccountType}
            itemToString={accountTypeItemToString}
            areItemsEqual={areSelectOptionsEqual}
            placeholder="Select account type"
            onChange={(selection) =>
              vm.setAccountType(
                (selection?.id as BankAccountTypeOption | undefined) ?? '',
              )
            }
            testId="edit-refund-account-type"
          />
        </FormGroupContainer>
        <TextInputContainer
          model={vm}
          field="bankName"
          name="bankName"
          label="Bank name"
          sublabel="(optional)"
          error={vm.validation.getError('bankName')}
          data-test="edit-refund-bank-name"
        />
        <TextInputContainer
          model={vm}
          field="routingNumber"
          name="routingNumber"
          label="Routing number"
          error={vm.validation.getError('routingNumber')}
          data-test="edit-refund-routing-number"
        />
        <TextInputContainer
          model={vm}
          field="accountNumber"
          name="accountNumber"
          label="Account number"
          error={vm.validation.getError('accountNumber')}
          data-test="edit-refund-account-number"
        />
      </EditRefundDirectDepositSection>
    )
  },
)

interface CheckFieldsContainerProps {
  vm: CheckDeliveryMethodEditorViewModel
}

const CheckFieldsContainer = observer(function CheckFieldsContainer({
  vm,
}: CheckFieldsContainerProps) {
  return (
    <>
      <EditRefundSameAddressCheckbox
        checked={vm.useSameAddressAsContact}
        onToggle={vm.toggleUseSameAddressAsContact}
      />
      {!vm.useSameAddressAsContact && (
        <InfoGatheringDialogFieldGrid>
          <PhysicalAddressEditorContainer
            vm={vm.physicalAddressVm}
            testId="edit-refund"
          />
        </InfoGatheringDialogFieldGrid>
      )}
    </>
  )
})
